import {
  HTTP_BAD_GATEWAY,
  HTTP_BAD_REQUEST,
  HTTP_CONFLICT,
  HTTP_FORBIDDEN,
  HTTP_GATEWAY_TIMEOUT,
  HTTP_INTERNAL_SERVER_ERROR,
  HTTP_NOT_FOUND,
  HTTP_SERVICE_UNAVAILABLE,
  HTTP_UNAUTHORIZED,
  HTTP_UNPROCESSABLE_ENTITY,
} from '../constants/common';
import { hasStringValue, hasValue } from './equality';

export type RequestFailure = {
  status: number | undefined;
  message: string | undefined;
};

const NO_RESPONSE_MESSAGE = "We couldn't reach Slabstack. Check your connection and try again.";
const SERVER_FAILURE_MESSAGE = 'Something went wrong on our side. Try again in a few minutes.';
const INVALID_VALUES_MESSAGE = "Some values couldn't be saved. Check the form and try again.";

type BareStatus = {
  statusText: string;
  message: string;
};

// The backend sends http.StatusText(code) when a handler sets no message of its own.
const BARE_STATUSES = new Map<number, BareStatus>([
  [HTTP_BAD_REQUEST, { statusText: 'Bad Request', message: INVALID_VALUES_MESSAGE }],
  [
    HTTP_UNAUTHORIZED,
    { statusText: 'Unauthorized', message: 'Your session expired. Sign in again.' },
  ],
  [HTTP_FORBIDDEN, { statusText: 'Forbidden', message: "You don't have permission to do this." }],
  [
    HTTP_NOT_FOUND,
    {
      statusText: 'Not Found',
      message: 'This item was deleted or you no longer have access to it.',
    },
  ],
  [
    HTTP_CONFLICT,
    {
      statusText: 'Conflict',
      message: 'This item was changed by someone else. Reload it and try again.',
    },
  ],
  [
    HTTP_UNPROCESSABLE_ENTITY,
    { statusText: 'Unprocessable Entity', message: INVALID_VALUES_MESSAGE },
  ],
  [
    HTTP_INTERNAL_SERVER_ERROR,
    { statusText: 'Internal Server Error', message: SERVER_FAILURE_MESSAGE },
  ],
  [HTTP_BAD_GATEWAY, { statusText: 'Bad Gateway', message: SERVER_FAILURE_MESSAGE }],
  [
    HTTP_SERVICE_UNAVAILABLE,
    { statusText: 'Service Unavailable', message: SERVER_FAILURE_MESSAGE },
  ],
  [HTTP_GATEWAY_TIMEOUT, { statusText: 'Gateway Timeout', message: SERVER_FAILURE_MESSAGE }],
]);

export const requestErrorMessage = ({ status, message }: RequestFailure): string | undefined => {
  if (!hasValue(status)) return NO_RESPONSE_MESSAGE;

  const bareStatus = BARE_STATUSES.get(status);
  if (hasStringValue(message) && message !== bareStatus?.statusText) return message;

  return bareStatus?.message;
};
